<template>
  <div>
    <!-- Marks Records List Card -->
    <b-card class="marks-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div class="table-header-left">
          <h5 class="mb-0 marks-record-title">Marks Records</h5>
          <small class="text-muted">View and review all marks records</small>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-bar d-flex flex-wrap align-items-center">
        <div class="filter-item">
          <v-select
            v-model="filters.classID"
            :options="classes"
            label="name"
            :reduce="val => val.id"
            placeholder="Class"
            :clearable="true"
            @input="onClassChange"
            class="filter-control"
          />
        </div>
        <div class="filter-item section-filter">
          <v-select
            v-model="filters.sectionID"
            :options="sections"
            label="name"
            :reduce="val => val.id"
            placeholder="Section (All)"
            :clearable="true"
            :disabled="!filters.classID"
            @input="onFilterChange"
            class="filter-control"
          />
        </div>
        <div class="filter-item">
          <v-select
            v-model="filters.subjectID"
            :options="subjects"
            label="subject"
            :reduce="val => val.id"
            placeholder="Subject"
            :clearable="true"
            :disabled="!filters.classID"
            @input="onFilterChange"
            class="filter-control"
          />
        </div>
        <div class="filter-item exam-type-filter">
          <v-select
            v-model="filters.examTypeID"
            :options="examTypes"
            label="examType"
            :reduce="val => val.id"
            placeholder="Exam Type"
            :clearable="true"
            @input="onFilterChange"
            class="filter-control"
          />
        </div>
        <div class="filter-item">
          <v-select
            v-model="filters.sessionID"
            :options="sessions"
            label="session"
            :reduce="val => val.id"
            placeholder="Session"
            :clearable="true"
            @input="onFilterChange"
            class="filter-control"
          />
        </div>
        <div class="filter-item student-filter">
          <v-select
            v-model="filters.sID"
            :options="studentOptions"
            :get-option-label="studentOptionLabel"
            :reduce="val => val.id"
            placeholder="Search student"
            :clearable="true"
            :loading="studentLoading"
            @search="onStudentSearch"
            @input="onFilterChange"
            class="filter-control"
          >
            <template #option="{ name, gr }">
              <div class="student-option">
                <strong v-if="gr">{{ gr }}</strong>
                <span v-if="gr"> - </span>
                <span>{{ name }}</span>
              </div>
            </template>
            <template #selected-option="{ name, gr }">
              <div class="student-option-selected">
                <strong v-if="gr">{{ gr }}</strong>
                <span v-if="gr"> - </span>
                <span>{{ name }}</span>
              </div>
            </template>
          </v-select>
        </div>
        <div class="filter-item print-button-filter">
          <b-button
            variant="outline-primary"
            class="btn-icon"
            @click="openPrintReport()"
            :disabled="printing"
          >
            <b-spinner v-if="printing" small variant="primary" />
            <feather-icon v-else icon="PrinterIcon" class="mr-50" />
            <span v-if="!printing">Print</span>
          </b-button>
          
          <b-button
            variant="outline-success"
            class="btn-icon ml-1"
            @click="exportTabulationSheet()"
            :disabled="exporting"
          >
            <b-spinner v-if="exporting" small variant="success" />
            <feather-icon v-else icon="DownloadIcon" class="mr-50" />
            <span v-if="!exporting">Export</span>
          </b-button>
        </div>
        <div 
          v-if="rights && (rights.add || rights.edit || rights.delete)"
          class="filter-item session-setting-filter">
          <div class="session-setting-wrapper d-flex align-items-center">
            <span class="session-setting-text mr-2">Session Setting</span>
            <feather-icon
              class="cursor-pointer session-settings-icon"
              icon="SettingsIcon"
              size="20"
              v-b-tooltip.hover.left
              title="Manage Sessions"
              @click="openSession()"
            />
          </div>
        </div>
      </div>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="option => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>
        
        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalCount }}</strong>
          </span>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar" @scroll="syncScroll">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="marks-records-table"
            :items="marksRecords"
            :fields="tableFields"
            :busy="tableBusy"
            :responsive="false"
            hover
            show-empty
            :empty-text="tableEmptyText"
          >
          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td v-for="(field, index) in tableFields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(sno)="{ index }">
            <span class="text-muted">
              {{ (currentPage - 1) * perPage + index + 1 }}
            </span>
          </template>

          <template #cell(studentName)="data">
            <div class="d-flex flex-column">
              <span class="font-weight-bold mb-1">{{ data.item.studentName || 'N/A' }}</span>
              <b-badge variant="light-primary" pill class="align-self-start">
                {{ data.item.studentGR || 'N/A' }}
              </b-badge>
            </div>
          </template>

          <template #cell(classSection)="data">
            <div class="d-flex flex-column">
              <span class="font-weight-medium">{{ data.item.className || 'N/A' }}</span>
              <small class="text-muted">{{ data.item.sectionName || 'N/A' }}</small>
            </div>
          </template>

          <template #cell(subjectName)="data">
            <b-badge class="subject-badge" pill>
              {{ data.item.subjectName || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(examTypeName)="data">
            <b-badge variant="light-primary" pill>
              {{ data.item.examTypeName || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(teacherName)="data">
            <span class="font-weight-medium">{{ data.item.teacherName || 'N/A' }}</span>
          </template>

          <template #cell(obtainedMarks)="data">
            <div 
              class="marks-circle obtained-marks"
              :class="{ 'grade-failed': isGradeF(data.item.meta_sub_grade) }"
            >
              <span class="marks-value">{{ data.item.obtainedMarks || 0 }}</span>
            </div>
          </template>

          <template #cell(totalMarks)="data">
            <div 
              class="marks-circle total-marks"
              :class="{ 'grade-failed': isGradeF(data.item.meta_sub_grade) }"
            >
              <span class="marks-value">{{ data.item.totalMarks || 0 }}</span>
            </div>
          </template>

          <template #cell(percentage)="data">
            <b-badge 
              :variant="getPercentageVariant(data.item.obtainedMarks, data.item.totalMarks)"
              pill
            >
              {{ getPercentage(data.item.obtainedMarks, data.item.totalMarks) }}%
            </b-badge>
          </template>

          <template #cell(rank)="data">
            <b-badge class="rank-badge" pill>
              {{ data.item.rank || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(meta_sub_grade)="data">
            <b-badge 
              class="grade-badge" 
              :class="{ 'grade-failed-badge': isGradeF(data.item.meta_sub_grade) }"
              pill
            >
              {{ data.item.meta_sub_grade || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(status)="data">
            <b-badge 
              class="status-badge"
              :class="getStatusClass(data.item.status)"
              pill
            >
              {{ data.item.status || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="d-flex justify-content-center">
              <feather-icon
                v-if="rights.delete"
                icon="Trash2Icon"
                size="16"
                class="cursor-pointer text-danger"
                v-b-tooltip.hover
                title="Delete Record"
                @click="deleteMarksRecord(data.item)"
              />
            </div>
          </template>

     
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- Session Management Sidebar -->
    <b-sidebar
      id="sidebar-session"
      bg-variant="white"
      v-model="sessionbar"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Manage Sessions</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="sessionbar = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="d-flex align-items-center">
          <b-col md="10">
            <b-form-group
              label="Name"
              invalid-feedback="Session name is required"
            >
              <b-form-input
                ref="sess_name"
                v-model="sessionObj.session"
                placeholder="Enter session name"
                @focusout="SessName()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-button
              variant="success"
              class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
              @click="saveSession()"
              :disabled="savingacc"
            >
              <b-spinner v-if="savingacc" small type="grow" />
              <feather-icon v-else :icon="accIcon" />
            </b-button>
          </b-col>
        </b-row>
        <b-table
          class="mt-1"
          show-empty
          :items="sessions"
          :fields="sessFields"
          responsive
          hover
        >
          <template #head(actions)="data">
            <div class="text-right">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(session)="data">
            <b-badge
              variant="light-primary"
              v-if="
                $store.state.currentBranch.organizationType == 'coaching' &&
                data.item.isCurrentSession
              "
            >
              {{ data.item.session }}
            </b-badge>
            <span v-else>{{ data.item.session }}</span>
          </template>

          <template #cell(actions)="data">
            <div class="text-right">
              <!-- <b-button
                v-if="rights.edit"
                variant="primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="editSess(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button> -->
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="deleteSess(data.item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
              <b-button
                v-if="
                  $store.state.currentBranch.organizationType == 'coaching' &&
                  !data.item.isCurrentSession
                "
                variant="outline-primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="setCurrentSess(data.item.id)"
              >
                <feather-icon icon="CheckCircleIcon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-form>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BBadge,
  BPagination,
  BFormInput,
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BRow,
  BCol,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import axios from "axios";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BPagination,
    BFormInput,
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    BRow,
    BCol,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      dataLoading: false,
      rights: {},
      marksRecords: [],
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      totalPages: 1,
      filters: {
        classID: null,
        sectionID: null,
        subjectID: null,
        examTypeID: null,
        sessionID: null,
        sID: null,
        tID: null, // teacher ID (staffID) filter
      },
      classes: [],
      classesData: [],
      sections: [],
      subjects: [],
      examTypes: [],
      sessions: [],
      studentOptions: [],
      studentLoading: false,
      printing: false,
      exporting: false,
      sessionbar: false,
      sessionObj: {
        id: 0,
        session: "",
        campusID: this.$store.state.userData.cId,
      },
      savingacc: false,
      accIcon: "PlusIcon",
      sessFields: [
        { label: "Name", key: "session" },
        { key: "actions", label: "actions" },
      ],
      pageSizeOptions: [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '50', value: 50 },
        { label: '100', value: 100 },
      ],
      tableFields: [
        { label: "S.No", key: "sno", sortable: false, thStyle: { width: '70px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Student", key: "studentName", sortable: false, thStyle: { minWidth: '200px' } },
        { label: "Class / Section", key: "classSection", sortable: false, thStyle: { width: '150px' } },
        { label: "Subject", key: "subjectName", sortable: false, thStyle: { minWidth: '150px' } },
        { label: "Exam Type", key: "examTypeName", sortable: false, thStyle: { minWidth: '150px' } },
        { label: "Session", key: "sessionName", sortable: false, thStyle: { minWidth: '120px' } },
        { label: "Teacher", key: "teacherName", sortable: false, thStyle: { minWidth: '150px' } },
        { label: "Obtained", key: "obtainedMarks", sortable: false, thStyle: { width: '100px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Total", key: "totalMarks", sortable: false, thStyle: { width: '100px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Percentage", key: "percentage", sortable: false, thStyle: { width: '120px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Rank", key: "rank", sortable: false, thStyle: { width: '100px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Grade", key: "meta_sub_grade", sortable: false, thStyle: { width: '100px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Status", key: "status", sortable: false, thStyle: { width: '100px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "Actions", key: "actions", sortable: false, thStyle: { width: '80px', textAlign: 'center' }, tdClass: 'text-center' },
      ],
    };
  },
  computed: {
    tableBusy() {
      return this.dataLoading;
    },
    tableEmptyText() {
      return this.tableBusy ? '' : 'No marks records found';
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  async created() {
    // Initialize rights
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);
    
    // Get rights for current route
    const routeName = this.$route.name;
    if (this.$store.state.rights[routeName]) {
      this.rights = this.$store.state.rights[routeName];
    }
    
    await Promise.all([
      this.loadClasses(),
      this.loadExamTypes(),
      this.loadSessions(),
    ]);

    // Try to load staff/teacher ID for current user so we can
    // filter marks by teacher (tID) similar to marksEntrySimple.
    await this.loadStaffIdForCurrentUser();

    await this.loadMarksData();
  },
  mounted() {
    this.updateScrollbarWidth();
    window.addEventListener('resize', this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollbarWidth);
  },
  methods: {
    ...mapActions({ get: "get" }),

    async deleteMarksRecord(item) {
      const result = await Swal.fire({
        title: 'Delete Marks Record?',
        text: this.$t("You won't be able to revert this!"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ea5455', // Danger color
        cancelButtonColor: '#82868b',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          const url = `${this.$store.state.domain}TabulationSheetMarks/DeleteMarksRecord/${item.id}/${item.marksGridID}/${item.sID}`;
          
          await axios.delete(url, {
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              db: this.$store.state.userData.db
            }
          });

          this.$bvToast.toast('Record deleted successfully', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
          
          // Reload data to reflect changes
          this.loadMarksData();
        } catch (error) {
          console.error("Delete error:", error);
          this.$bvToast.toast('Failed to delete record. Please try again.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      }
    },

    openPrintReport() {
      // Validate required filters
      if (!this.filters.classID) {
        this.$bvToast.toast('Please select a class', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.filters.examTypeID) {
        this.$bvToast.toast('Please select an exam type', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.filters.sessionID) {
        this.$bvToast.toast('Please select a session', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.filters.subjectID) {
        this.$bvToast.toast('Please select a subject', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.printing = true;

      try {
        const routeData = this.$router.resolve({
          name: "marksrecordprint",
          query: {
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
            classId: this.filters.classID,
            sectionId: this.filters.sectionID || 0,
            examTypeId: this.filters.examTypeID,
            sessionId: this.filters.sessionID,
            subjectIds: this.filters.subjectID,
          },
        });
        window.open(routeData.href, "_blank");

        this.$bvToast.toast("Report opened in new tab", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Print error:", error);
        this.$bvToast.toast("Failed to open report. Please try again.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.printing = false;
      }
    },

    async exportTabulationSheet() {
      // Validate required filters
      if (!this.filters.classID) {
        this.$bvToast.toast('Please select a class', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.filters.examTypeID) {
        this.$bvToast.toast('Please select an exam type', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.filters.sessionID) {
        this.$bvToast.toast('Please select a session', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.filters.subjectID) {
        this.$bvToast.toast('Please select a subject', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.exporting = true;

      try {
        const params = {
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          classId: this.filters.classID,
          sectionId: this.filters.sectionID || 0,
          examTypeId: this.filters.examTypeID,
          sessionId: this.filters.sessionID,
          subjectIds: this.filters.subjectID,
          isExport: true
        };

        const url = `${this.$store.state.domain}TabulationSheetMarks/GetTabulationSheetExport`;
        
        const response = await axios.get(url, {
          params: params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            db: this.$store.state.userData.db
          }
        });

        let exportData = [];
        let subjectStats = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            exportData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            exportData = response.data.data;
            subjectStats = Array.isArray(response.data.subjectStats)
              ? response.data.subjectStats
              : [];
          } else if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
            exportData = response.data.data.data;
            subjectStats = Array.isArray(response.data.data.subjectStats)
              ? response.data.data.subjectStats
              : Array.isArray(response.data.subjectStats)
              ? response.data.subjectStats
              : [];
          }

          if (!subjectStats.length && Array.isArray(response.data.subjectStats)) {
            subjectStats = response.data.subjectStats;
          }
        }

        if (exportData.length > 0) {
          const ws = XLSX.utils.json_to_sheet(exportData);

          if (subjectStats.length > 0) {
            const startRow = exportData.length + 3; // blank row after table, then header
            XLSX.utils.sheet_add_aoa(
              ws,
              [
                ["Subject Stats"],
                [
                  "Subject Name",
                  "Min Marks",
                  "Max Marks",
                  "Average Marks",
                  "Student Count",
                ],
                ...subjectStats.map((stat) => [
                  stat.subjectName || "",
                  stat.minMarks != null ? Number(stat.minMarks) : "",
                  stat.maxMarks != null ? Number(stat.maxMarks) : "",
                  stat.averageMarks != null ? Number(stat.averageMarks) : "",
                  stat.studentCount != null ? Number(stat.studentCount) : "",
                ]),
              ],
              { origin: `A${startRow}` }
            );
          }

          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "TabulationSheet");
          XLSX.writeFile(wb, "TabulationSheet_Export.xlsx");

          this.$bvToast.toast('Export completed successfully', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
        } else {
             this.$bvToast.toast('No data found to export', {
              title: 'Info',
              variant: 'info',
              solid: true
            });
        }

      } catch (error) {
        console.error('Export error:', error);
        this.$bvToast.toast('Failed to export. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.exporting = false;
      }
    },

    async loadStaffIdForCurrentUser() {
      try {
        const userLinkID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.localID ||
          this.$store.state.userData.userLinkID ||
          0;
        const cID = this.$store.state.userData.cId;
        const dbName = encodeURIComponent(this.$store.state.userData.db);

        if (!userLinkID || !cID) {
          return;
        }

        const staffUrl = `${this.$store.state.domain}Staff/GetByUserLinkID?db=${dbName}&userLinkID=${userLinkID}&cID=${cID}`;

        const staffResponse = await this.get({
          url: staffUrl,
          token: this.$store.state.userData.token,
        });

        let staffID = null;
        if (staffResponse && staffResponse.id) {
          staffID = staffResponse.id;
        } else if (staffResponse && staffResponse.data && staffResponse.data.id) {
          staffID = staffResponse.data.id;
        } else if (
          staffResponse &&
          Array.isArray(staffResponse) &&
          staffResponse.length > 0 &&
          staffResponse[0].id
        ) {
          staffID = staffResponse[0].id;
        } else if (
          staffResponse &&
          Array.isArray(staffResponse.data) &&
          staffResponse.data.length > 0 &&
          staffResponse.data[0].id
        ) {
          staffID = staffResponse.data[0].id;
        }

        if (staffID) {
          // Apply teacher filter so marks list is scoped to this staff member
          this.filters.tID = staffID;
        }
      } catch (error) {
        console.error("Error loading staff ID for marks record:", error);
      }
    },

    async loadMarksData() {
      this.dataLoading = true;
      this.marksRecords = [];

      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          page: this.currentPage,
          pageSize: this.perPage,
        });

        if (this.filters.classID) params.append('classID', this.filters.classID);
        // Always pass sectionID: 0 for all sections, or specific section ID
        params.append('sectionID', this.filters.sectionID || 0);
        if (this.filters.subjectID) params.append('subjectID', this.filters.subjectID);
        if (this.filters.examTypeID) params.append('examTypeID', this.filters.examTypeID);
        if (this.filters.sessionID) params.append('sessionID', this.filters.sessionID);
        if (this.filters.sID) params.append('sID', this.filters.sID);
        if (this.filters.tID) params.append('tID', this.filters.tID);

        const url = `${this.$store.state.domain}TabulationSheetMarks/GetAllMarksData?${params.toString()}`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${this.$store.state.userData.token}` },
        });

        const payload = res?.data ?? res ?? {};
        const container = payload?.data;

        // Extract list
        let list = [];
        if (Array.isArray(container?.data)) {
          list = container.data;
        } else if (Array.isArray(container)) {
          list = container;
        } else if (Array.isArray(payload?.data)) {
          list = payload.data;
        } else if (Array.isArray(payload)) {
          list = payload;
        }

        const pickNumber = (...vals) => {
          for (const v of vals) {
            const n = Number(v);
            if (v !== undefined && v !== null && !Number.isNaN(n)) return n;
          }
          return null;
        };

        const totalRecords =
          pickNumber(
            payload?.totalCount,
            payload?.totalRecords,
            payload?.total,
            container?.totalCount,
            container?.totalRecords,
            payload?.data?.totalCount,
            payload?.data?.totalRecords,
            payload?.data?.total,
            payload?.pagination?.totalCount,
            payload?.pagination?.totalRecords,
            payload?.meta?.totalCount,
            payload?.meta?.totalRecords
          ) ?? list.length;

        const pageSizeFromApi =
          pickNumber(
            payload?.pageSize,
            payload?.page_size,
            payload?.perPage,
            container?.pageSize,
            container?.page_size,
            container?.perPage,
            payload?.data?.pageSize,
            payload?.data?.page_size,
            payload?.data?.perPage,
            payload?.pagination?.pageSize,
            payload?.pagination?.page_size,
            payload?.meta?.pageSize
          ) || this.perPage;

        const totalPages =
          pickNumber(
            payload?.totalPages,
            container?.totalPages,
            payload?.data?.totalPages,
            payload?.data?.pagination?.totalPages,
            payload?.meta?.totalPages
          ) ||
          Math.ceil((totalRecords || list.length) / (pageSizeFromApi || this.perPage)) ||
          1;

        let pageFromApi =
          pickNumber(
            payload?.page,
            payload?.currentPage,
            container?.page,
            container?.currentPage,
            payload?.data?.page,
            payload?.data?.currentPage,
            payload?.data?.pagination?.page,
            payload?.meta?.page
          ) || this.currentPage || 1;

        if (pageFromApi > totalPages) pageFromApi = totalPages;
        if (pageFromApi < 1) pageFromApi = 1;

        this.marksRecords = list;
        this.totalCount = totalRecords;
        this.totalPages = totalPages;
        this.perPage = pageSizeFromApi || this.perPage;
        this.currentPage = pageFromApi;
        this.updateScrollbarWidth();
      } catch (error) {
        console.error("Error loading marks data:", error);
        this.$bvToast.toast("Failed to load marks records", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.marksRecords = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadMarksData();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadMarksData();
    },

    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector('.vs__dropdown-toggle');
          if (toggle) {
            toggle.blur();
          }
        }
      });
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadMarksData();
    },

    onClassChange(classId) {
      this.filters.sectionID = null;
      this.filters.subjectID = null;
      if (classId) {
        this.loadSections();
        this.loadSubjects();
      } else {
        this.sections = [];
        this.subjects = [];
      }
      this.onFilterChange();
    },

    async onStudentSearch(search) {
      if (!search || search.trim().length < 2) return;
      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(search)}&type=`,
          token: this.$store.state.userData.token,
        };
        const res = await this.get(obj);
        const data = res?.data || res || [];
        this.studentOptions = Array.isArray(data)
          ? data.map(s => ({
              id: s.id,
              name: s.name || s.studentName || 'Unknown',
              gr:
                s.grNo ||
                s.grno ||
                s.grNumber ||
                s.grnumber ||
                s.gr ||
                s.grn ||
                s.grNum ||
                s.grnum ||
                s.gr_code ||
                s.grCode ||
                s.grno_code ||
                s.grNo_code ||
                null,
            }))
          : [];
      } catch (error) {
        console.error('Error searching students:', error);
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    async loadClasses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const classesData = response.data || response;
        this.classesData = Array.isArray(classesData) ? classesData : [];
        this.classes = this.classesData.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },

    async loadSections() {
      if (!this.filters.classID) {
        this.sections = [];
        return;
      }
      try {
        const classItem = this.classesData.find((c) => c.cls && c.cls.id === this.filters.classID);
        if (classItem && classItem.secSubjectsData && Array.isArray(classItem.secSubjectsData)) {
          const sectionMap = new Map();
          classItem.secSubjectsData.forEach((sec) => {
            if (sec.sectionID && !sectionMap.has(sec.sectionID)) {
              sectionMap.set(sec.sectionID, {
                id: sec.sectionID,
                name: sec.section || sec.sectionName || `Section ${sec.sectionID}`,
              });
            }
          });
          this.sections = Array.from(sectionMap.values());
        } else {
          // Fallback: try to load sections from API
          const obj = {
            url:
              this.$store.state.domain +
              "Sections?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId +
              "&classID=" +
              this.filters.classID,
            token: this.$store.state.userData.token,
          };
          const response = await this.get(obj);
          const sectionsData = response.data || response;
          this.sections = Array.isArray(sectionsData)
            ? sectionsData.map((sec) => ({
                id: sec.id || sec.sectionID,
                name: sec.section || sec.name || sec.sectionName || `Section ${sec.id || sec.sectionID}`,
              }))
            : [];
        }
      } catch (error) {
        console.error("Error loading sections:", error);
        this.sections = [];
      }
    },

    async loadSubjects() {
      if (!this.filters.classID) {
        this.subjects = [];
        return;
      }
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        // Handle response structure: { data: [...], status: "success" }
        if (response && response.data && Array.isArray(response.data)) {
          this.subjects = response.data;
        } else if (Array.isArray(response)) {
          this.subjects = response;
        } else {
          this.subjects = [];
        }
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.subjects = [];
      }
    },

    studentOptionLabel(option) {
      if (!option) return '';
      if (option.gr) return `${option.gr} - ${option.name || ''}`.trim();
      return option.name || '';
    },

    async loadExamTypes() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&status=active&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response.data || response;
        this.examTypes = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading exam types:", error);
        this.examTypes = [];
      }
    },

    async loadSessions() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response.data || response;
        this.sessions = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },
    openSession() {
      this.accIcon = "PlusIcon";
      this.sessionbar = true;
      this.sessionObj = {
        id: 0,
        session: "",
        campusID: this.$store.state.userData.cId,
      };
      this.$nextTick(() => {
        const elem = this.$refs["sess_name"];
        if (elem) {
          elem.state = undefined;
        }
      });
    },
    SessName() {
      const elem = this.$refs["sess_name"];
      if (!elem) return true;
      if (this.sessionObj.session.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveSession() {
      if (this.SessName() == true) {
        this.savingacc = true;
        if (this.sessionObj.id == 0) {
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Sessions?db=" +
              this.$store.state.userData.db,
            body: this.sessionObj,
            message: "Session added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          var status = await this.put({
            url:
              this.$store.state.domain +
              "Sessions/" +
              this.sessionObj.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Session updated successfully.",
            context: this,
            body: this.sessionObj,
            token: this.$store.state.userData.token,
          });
        }
        this.savingacc = false;
        if (status) {
          await this.loadSessions();
          const elem = this.$refs["sess_name"];
          if (elem) {
            elem.state = undefined;
          }
          this.sessionObj = {
            id: 0,
            session: "",
            campusID: this.$store.state.userData.cId,
          };
        }
      }
    },
    async editSess(item) {
      this.sessionObj = { ...item };
      this.accIcon = "Edit2Icon";
      this.$nextTick(() => {
        const elem = this.$refs["sess_name"];
        if (elem) {
          elem.state = undefined;
        }
      });
    },
    async deleteSess(item) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Sessions/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Session removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) await this.loadSessions();
      }
    },
    async setCurrentSess(id) {
      var status = await this.post({
        url:
          this.$store.state.domain +
          "Sessions/SetCurrentSession?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&sessionID=" +
          id,
        body: this.sessionObj,
        message: "Session marked as current",
        context: this,
        token: this.$store.state.userData.token,
      });

      if (status) {
        await this.loadSessions();
      }
    },

    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      if (!tableWrapper || !topScrollbar) return;

      if (event && event.target === tableWrapper) {
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      } else if (event && event.target === topScrollbar) {
        tableWrapper.scrollLeft = topScrollbar.scrollLeft;
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar && topScrollbar.querySelector('.table-scrollbar-content');
        if (tableWrapper && scrollContent) {
          scrollContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    getPercentage(obtained, total) {
      if (!total || total === 0) return 0;
      return ((obtained / total) * 100).toFixed(1);
    },

    getPercentageVariant(obtained, total) {
      const percentage = this.getPercentage(obtained, total);
      if (percentage >= 80) return "light-success";
      if (percentage >= 60) return "light-warning";
      return "light-danger";
    },

    getPercentageClass(obtained, total) {
      const percentage = parseFloat(this.getPercentage(obtained, total));
      if (percentage >= 80) return "percentage-high";
      if (percentage >= 60) return "percentage-medium";
      return "percentage-low";
    },

    getStatusVariant(status) {
      if (!status) return "light-secondary";
      const statusLower = status.toLowerCase();
      if (statusLower.includes('pass') || statusLower.includes('active')) return "light-success";
      if (statusLower.includes('fail') || statusLower.includes('inactive')) return "light-danger";
      return "light-secondary";
    },

    getStatusClass(status) {
      if (!status) return "status-neutral";
      const statusLower = status.toLowerCase();
      if (statusLower.includes('pass') || statusLower.includes('active')) return "status-passed";
      if (statusLower.includes('fail') || statusLower.includes('inactive')) return "status-failed";
      return "status-neutral";
    },

    isGradeF(grade) {
      if (!grade) return false;
      return grade.toString().toUpperCase().trim() === 'F';
    },

    formatType(type) {
      if (!type) return 'N/A';
      return type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

// Marks List Card - Matching commentbank.vue style
.marks-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

// Table Wrapper for Horizontal Scroll
.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.marks-record-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}



// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;
    
    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
  
  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }
  
  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

.filters-bar {
  padding: 0 0 0.25rem 0;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.filter-item {
  min-width: 170px;
}

.section-filter {
  min-width: 120px;
  max-width: 140px;
}

.student-filter {
  min-width: 260px;
}

.exam-type-filter {
  min-width: 210px;
}

.session-setting-filter {
  min-width: auto;
  flex-shrink: 0;
  margin-left: auto;
  
  @media (max-width: 992px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }
}

.filters-bar .vs__dropdown-toggle,
.filters-bar .form-control {
  border-radius: 14px;
}

.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 12px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus,
  &:focus-within {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

.filter-control.v-select {
  width: 100%;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;

  &::before,
  &::after {
    display: none !important;
  }

  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 12px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.5rem 0.75rem !important;
    background: #fff !important;
    transition: all 0.2s ease;
    box-shadow: none !important;
    margin: 0 !important;

    &:hover {
      border-color: #b8a9ff !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
      background: #fff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
      background: #fff !important;
    }
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__search {
    padding: 0 !important;
    margin: 0 !important;
  }
}

.student-option,
.student-option-selected {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;
  
  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    
    &:hover {
      border-color: #b8a9ff !important;
    }
    
    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }
  
  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }
  
  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }
  
  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    
    &--selected {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
      color: #ffffff !important;
    }
    
    &:hover {
      background: #f3f0ff !important;
      color: #7c5dfa !important;
    }
  }
}

// Pagination Count Display
.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;
  
  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
  
  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

// Compact Pagination Wrapper
.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  
  ::v-deep .pagination {
    margin-bottom: 0;
    
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }
    
    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }
    
    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

.marks-records-table {
  margin-top: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  width: 100%;
  min-width: 1200px;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      
      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;
      
      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }
  }
}

.empty-state-box {
  padding: 4rem 2rem;
  text-align: center;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
  margin: 2rem 0;
  animation: slideUpFade 0.5s ease-out both;
}

.empty-state-content {
  max-width: 500px;
  margin: 0 auto;
}

.empty-state-icon {
  margin-bottom: 1.5rem;
  color: #9ca3af;
  
  svg {
    stroke-width: 1.5;
  }
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.empty-state-submessage {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 400;
  line-height: 1.5;
}

.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 0.15s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.45s; }
  &:nth-child(5) { animation-delay: 0.6s; }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

/* Column-specific skeleton widths for marks record columns */
.skeleton-cell:nth-child(1) .skeleton-loader { width: 60%; margin: 0 auto; }
.skeleton-cell:nth-child(2) .skeleton-loader { width: 160px; }
.skeleton-cell:nth-child(3) .skeleton-loader { width: 110px; margin: 0 auto; }
.skeleton-cell:nth-child(4) .skeleton-loader { width: 100px; margin: 0 auto; }
.skeleton-cell:nth-child(5) .skeleton-loader { width: 150px; }
.skeleton-cell:nth-child(6) .skeleton-loader { width: 130px; }
.skeleton-cell:nth-child(7) .skeleton-loader { width: 150px; }
.skeleton-cell:nth-child(8) .skeleton-loader { width: 80px; margin: 0 auto; }
.skeleton-cell:nth-child(9) .skeleton-loader { width: 80px; margin: 0 auto; }
.skeleton-cell:nth-child(10) .skeleton-loader { width: 90px; margin: 0 auto; }
.skeleton-cell:nth-child(11) .skeleton-loader { width: 80px; margin: 0 auto; }
.skeleton-cell:nth-child(12) .skeleton-loader { width: 90px; margin: 0 auto; }

@keyframes skeletonShimmer {
  0% { background-position: -300% 0; }
  100% { background-position: 300% 0; }
}

@keyframes skeletonShine {
  0% { left: -100%; }
  50% { left: 100%; }
  100% { left: 100%; }
}

@keyframes skeletonColorWave {
  0%, 100% { opacity: 0.3; transform: scaleX(0.8); }
  50% { opacity: 0.6; transform: scaleX(1); }
}

@keyframes skeletonBorderGlow {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

@keyframes skeletonPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(0.998); }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.btn-modern-primary {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  border: none;
  color: #fff;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .marks-entry-card {
    padding: 1rem;
  }

  .table-scroll-wrapper {
    margin-top: 0.5rem;
  }

  .marks-records-table {
    min-width: 1200px;
  }

  .table-header {
    position: relative;
    width: 100%;
    
    .table-header-left {
      flex: 1;
      min-width: 0;
    }
  }

  .session-setting-wrapper {
    gap: 0.5rem;
    flex-shrink: 0;
    display: flex !important;
    align-items: center;
    padding: 0.5rem 0.75rem;
    height: 42px;
  }

  .session-setting-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: #5b21b6;
    white-space: nowrap;
  }

  .session-settings-icon {
    color: #7c5dfa;
    transition: all 0.2s ease;
    flex-shrink: 0;
    
    &:hover {
      color: #6d4cdb;
      transform: rotate(90deg);
    }
  }

  // Circular Marks Badges - Light width
  .marks-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    font-weight: 600;
    font-size: 0.875rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover::before {
      opacity: 1;
    }
    
    .marks-value {
      line-height: 1;
      position: relative;
      z-index: 1;
    }
    
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    }
  }

  .obtained-marks {
    background: rgba(6, 182, 212, 0.1);
    color: #0891b2 !important;
    border: 1px solid rgba(6, 182, 212, 0.2);
    
    &.grade-failed {
      background: rgba(239, 68, 68, 0.1) !important;
      color: #dc2626 !important;
      border: 1px solid rgba(239, 68, 68, 0.2) !important;
    }
    
    &:hover {
      background: rgba(6, 182, 212, 0.15);
      border-color: rgba(6, 182, 212, 0.3);
      
      &.grade-failed {
        background: rgba(239, 68, 68, 0.15) !important;
        border-color: rgba(239, 68, 68, 0.3) !important;
      }
    }
  }

  .total-marks {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706 !important;
    border: 1px solid rgba(245, 158, 11, 0.2);
    font-weight: 500;
    
    &.grade-failed {
      background: rgba(239, 68, 68, 0.1) !important;
      color: #dc2626 !important;
      border: 1px solid rgba(239, 68, 68, 0.2) !important;
    }
    
    &:hover {
      background: rgba(245, 158, 11, 0.15);
      border-color: rgba(245, 158, 11, 0.3);
      
      &.grade-failed {
        background: rgba(239, 68, 68, 0.15) !important;
        border-color: rgba(239, 68, 68, 0.3) !important;
      }
    }
  }

  // Subject Badge - Scoped to this page only
  .marks-records-table {
    ::v-deep .subject-badge,
    ::v-deep .subject-badge.badge,
    ::v-deep td .subject-badge,
    ::v-deep td .subject-badge.badge,
    ::v-deep tbody tr td .subject-badge,
    ::v-deep tbody tr td .subject-badge.badge {
      padding: 0.5rem 1rem !important;
      font-size: 0.875rem !important;
      font-weight: 600 !important;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
      background-color: #8b5cf6 !important;
      background-image: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
      color: #ffffff !important;
      border: none !important;
      box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3) !important;
      transition: all 0.3s ease !important;
      
      * {
        color: #ffffff !important;
      }
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4) !important;
        background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
        background-color: #7c3aed !important;
        background-image: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
      }
    }

    // Rank and Grade Badges - Scoped to this page only - Lightweight style
    ::v-deep .rank-badge,
    ::v-deep .rank-badge.badge,
    ::v-deep td .rank-badge,
    ::v-deep td .rank-badge.badge,
    ::v-deep tbody tr td .rank-badge,
    ::v-deep tbody tr td .rank-badge.badge {
      padding: 0.375rem 0.75rem !important;
      font-size: 0.8125rem !important;
      font-weight: 500 !important;
      background: rgba(245, 158, 11, 0.1) !important;
      background-color: rgba(245, 158, 11, 0.1) !important;
      color: #d97706 !important;
      border: 1px solid rgba(245, 158, 11, 0.2) !important;
      box-shadow: none !important;
      transition: all 0.3s ease !important;
      
      * {
        color: #d97706 !important;
      }
      
      &:hover {
        background: rgba(245, 158, 11, 0.15) !important;
        border-color: rgba(245, 158, 11, 0.3) !important;
      }
    }

    ::v-deep .grade-badge,
    ::v-deep .grade-badge.badge,
    ::v-deep td .grade-badge,
    ::v-deep td .grade-badge.badge,
    ::v-deep tbody tr td .grade-badge,
    ::v-deep tbody tr td .grade-badge.badge {
      padding: 0.375rem 0.75rem !important;
      font-size: 0.8125rem !important;
      font-weight: 500 !important;
      background: rgba(20, 184, 166, 0.1) !important;
      background-color: rgba(20, 184, 166, 0.1) !important;
      color: #0d9488 !important;
      border: 1px solid rgba(20, 184, 166, 0.2) !important;
      box-shadow: none !important;
      transition: all 0.3s ease !important;
      letter-spacing: 0.5px !important;
      
      * {
        color: #0d9488 !important;
      }
      
      &:hover {
        background: rgba(20, 184, 166, 0.15) !important;
        border-color: rgba(20, 184, 166, 0.3) !important;
      }
    }
  }
}
</style>

<style lang="scss">
.marks-records-table {
  .subject-badge,
  .subject-badge.badge,
  td .subject-badge,
  td .subject-badge.badge,
  tbody tr td .subject-badge,
  tbody tr td .subject-badge.badge {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.8125rem !important;
    font-weight: 500 !important;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
    background-color: #8b5cf6 !important;
    background-image: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
    color: #ffffff !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3) !important;
    transition: all 0.3s ease !important;
    
    * {
      color: #ffffff !important;
    }
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4) !important;
      background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
      background-color: #7c3aed !important;
      background-image: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
    }
  }

  .rank-badge,
  .rank-badge.badge,
  td .rank-badge,
  td .rank-badge.badge,
  tbody tr td .rank-badge,
  tbody tr td .rank-badge.badge {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.8125rem !important;
    font-weight: 500 !important;
    background: rgba(245, 158, 11, 0.1) !important;
    background-color: rgba(245, 158, 11, 0.1) !important;
    color: #d97706 !important;
    border: 1px solid rgba(245, 158, 11, 0.2) !important;
    box-shadow: none !important;
    transition: all 0.3s ease !important;
    
    * {
      color: #d97706 !important;
    }
    
    &:hover {
      background: rgba(245, 158, 11, 0.15) !important;
      border-color: rgba(245, 158, 11, 0.3) !important;
    }
  }

  .grade-badge,
  .grade-badge.badge,
  td .grade-badge,
  td .grade-badge.badge,
  tbody tr td .grade-badge,
  tbody tr td .grade-badge.badge {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.8125rem !important;
    font-weight: 500 !important;
    background: rgba(20, 184, 166, 0.1) !important;
    background-color: rgba(20, 184, 166, 0.1) !important;
    color: #0d9488 !important;
    border: 1px solid rgba(20, 184, 166, 0.2) !important;
    box-shadow: none !important;
    transition: all 0.3s ease !important;
    letter-spacing: 0.5px !important;
    
    * {
      color: #0d9488 !important;
    }
    
    &:hover {
      background: rgba(20, 184, 166, 0.15) !important;
      border-color: rgba(20, 184, 166, 0.3) !important;
    }
    
    &.grade-failed-badge {
      background: rgba(239, 68, 68, 0.1) !important;
      background-color: rgba(239, 68, 68, 0.1) !important;
      color: #dc2626 !important;
      border: 1px solid rgba(239, 68, 68, 0.2) !important;
      
      * {
        color: #dc2626 !important;
      }
      
      &:hover {
        background: rgba(239, 68, 68, 0.15) !important;
        border-color: rgba(239, 68, 68, 0.3) !important;
      }
    }
  }

  // Percentage Badge Styles - Lightweight
  .percentage-badge {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.8125rem !important;
    font-weight: 500 !important;
    border: 1px solid transparent !important;
    box-shadow: none !important;
    transition: all 0.3s ease !important;

    &.percentage-high {
      background: rgba(16, 185, 129, 0.1) !important;
      background-color: rgba(16, 185, 129, 0.1) !important;
      color: #059669 !important;
      border: 1px solid rgba(16, 185, 129, 0.2) !important;
      
      * {
        color: #059669 !important;
      }
      
      &:hover {
        background: rgba(16, 185, 129, 0.15) !important;
        border-color: rgba(16, 185, 129, 0.3) !important;
      }
    }

    &.percentage-medium {
      background: rgba(245, 158, 11, 0.1) !important;
      background-color: rgba(245, 158, 11, 0.1) !important;
      color: #d97706 !important;
      border: 1px solid rgba(245, 158, 11, 0.2) !important;
      
      * {
        color: #d97706 !important;
      }
      
      &:hover {
        background: rgba(245, 158, 11, 0.15) !important;
        border-color: rgba(245, 158, 11, 0.3) !important;
      }
    }

    &.percentage-low {
      background: rgba(239, 68, 68, 0.1) !important;
      background-color: rgba(239, 68, 68, 0.1) !important;
      color: #dc2626 !important;
      border: 1px solid rgba(239, 68, 68, 0.2) !important;
      
      * {
        color: #dc2626 !important;
      }
      
      &:hover {
        background: rgba(239, 68, 68, 0.15) !important;
        border-color: rgba(239, 68, 68, 0.3) !important;
      }
    }
  }

  // Status Badge Styles - Lightweight
  .status-badge {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.8125rem !important;
    font-weight: 500 !important;
    border: 1px solid transparent !important;
    box-shadow: none !important;
    transition: all 0.3s ease !important;

    &.status-passed {
      background: rgba(16, 185, 129, 0.1) !important;
      background-color: rgba(16, 185, 129, 0.1) !important;
      color: #059669 !important;
      border: 1px solid rgba(16, 185, 129, 0.2) !important;
      
      * {
        color: #059669 !important;
      }
      
      &:hover {
        background: rgba(16, 185, 129, 0.15) !important;
        border-color: rgba(16, 185, 129, 0.3) !important;
      }
    }

    &.status-failed {
      background: rgba(239, 68, 68, 0.1) !important;
      background-color: rgba(239, 68, 68, 0.1) !important;
      color: #dc2626 !important;
      border: 1px solid rgba(239, 68, 68, 0.2) !important;
      
      * {
        color: #dc2626 !important;
      }
      
      &:hover {
        background: rgba(239, 68, 68, 0.15) !important;
        border-color: rgba(239, 68, 68, 0.3) !important;
      }
    }

    &.status-neutral {
      background: rgba(107, 114, 128, 0.1) !important;
      background-color: rgba(107, 114, 128, 0.1) !important;
      color: #4b5563 !important;
      border: 1px solid rgba(107, 114, 128, 0.2) !important;
      
      * {
        color: #4b5563 !important;
      }
      
      &:hover {
        background: rgba(107, 114, 128, 0.15) !important;
        border-color: rgba(107, 114, 128, 0.3) !important;
      }
    }
  }
}
</style>
